import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Clock, BadgePoundSterling, ArrowUpRight, ArrowLeft, CalendarDays, FileText } from "lucide-react";
import { api, formatSalary, timeAgo } from "../lib/api";
import { sourceMeta } from "../lib/sources";
import Seo from "../components/Seo";
import { JobAlertControl } from "../components/JobAlertControl";

export default function ExtJobDetail({ embedded = false, embeddedSource = "", embeddedId = "", onClear = null }) {
  const params = useParams();
  const source = embeddedSource || params.source;
  const id = embeddedId || params.id;
  const [job, setJob] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setJob(null);
    setMissing(false);
    api.get(`/ext-jobs/${source}/${id}`).then((r) => setJob(r.data)).catch(() => setMissing(true));
  }, [source, id]);

  const meta = sourceMeta(job?.source || source);
  // return to the exact search position/filters the jobseeker came from
  let backTo = "/jobs";
  try { backTo = `/jobs${sessionStorage.getItem("jobs:return") || ""}`; } catch (_) { /* best-effort */ }

  if (missing) {
    return (
      <main className={embedded ? "px-2 py-10 text-center" : "max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center"} data-testid="ext-detail-gone">
        {!embedded && <Seo title="Listing no longer available | HiredToday" description="This partner listing has expired." path={`/jobs/ext/${source}/${id}`} noindex />}
        <h1 className="font-display text-2xl font-bold text-zinc-950">This listing has been filled or removed</h1>
        <p className="mt-2 text-sm text-zinc-500">
          Partner listings change quickly — the employer has likely closed this one. The good news: fresh roles arrive every day.
        </p>
        {embedded ? (
          <button onClick={onClear} data-testid="ext-gone-clear"
            className="mt-6 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-800 transition-colors duration-200">
            <ArrowLeft className="h-4 w-4" /> Back to search results
          </button>
        ) : (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/jobs" data-testid="ext-gone-browse"
            className="inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-800 transition-colors duration-200">
            Browse current jobs
          </Link>
          <Link to={backTo} data-testid="ext-gone-back"
            className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-700 text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-50 transition-colors duration-200">
            <ArrowLeft className="h-4 w-4" /> Back to search results
          </Link>
        </div>
        )}
      </main>
    );
  }

  if (!job) {
    return (
      <main className={embedded ? "px-2 py-6" : "max-w-3xl mx-auto px-4 sm:px-6 py-10"} data-testid="ext-detail-loading">
        <div className="border border-zinc-200 rounded-md p-6 animate-pulse">
          <div className="h-6 bg-zinc-100 rounded w-1/2 mb-3" />
          <div className="h-4 bg-zinc-100 rounded w-1/3" />
        </div>
      </main>
    );
  }

  const salary = formatSalary(job);
  const here = `/jobs/ext/${job.source}/${job.id}`;
  const applyUrl = `${api.defaults.baseURL}/go/${job.source}/${job.id}`;
  const extType = { full_time: "Full-time", part_time: "Part-time" }[job.contract_time] || "";
  const contract = job.contract_type ? job.contract_type[0].toUpperCase() + job.contract_type.slice(1).replace(/_/g, " ") : "";
  const closes = job.close_date ? new Date(job.close_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

  // SEO: only providers whose terms let us host the full content AND whose feed gives us
  // the complete description get an indexable page (currently CV-Library). Everyone else
  // stays noindex — thin/summary pages must not enter the index.
  // No JobPosting schema at this stage: the feed masks the real employer and Google
  // requires the genuine hiringOrganization — never present the provider as the employer.
  const indexable = job.source === "cvlibrary" && !!job.description;
  const maskedEmployer = (job.company_name || "").toLowerCase().replace(/[^a-z]/g, "")
    === meta.label.toLowerCase().replace(/[^a-z]/g, "");
  const employerShown = maskedEmployer ? "Employer not disclosed" : job.company_name;
  const plainDesc = (job.description || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const town = (job.location || "").split(",")[0]
    .replace(/\s*[A-Z]{1,2}\d{1,2}[A-Z]?(\s*\d[A-Z]{2})?\s*$/i, "").trim();
  const townSlug = town.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const seoTitle = `${job.title}${town ? ` in ${town}` : ""}${maskedEmployer ? "" : ` at ${job.company_name}`} | HiredToday`;
  const seoDesc = (`${job.title}${maskedEmployer ? "" : ` at ${job.company_name}`}${job.location ? ` in ${job.location}` : ""}. `
    + (plainDesc || `Listed with our partner ${meta.label}.`)).slice(0, 158);
  const empTypes = [
    { full_time: "FULL_TIME", part_time: "PART_TIME" }[job.contract_time] || "",
    job.contract_type === "contract" ? "CONTRACTOR" : "",
  ].filter(Boolean);
  const closesIso = job.close_date ? new Date(job.close_date).toISOString().slice(0, 10) : "";

  return (
    <main className={embedded ? "" : "max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14"} data-testid="ext-detail">
      {!embedded && <Seo title={seoTitle} description={seoDesc} path={here} noindex={!indexable} />}
      {!embedded && (
      <Link to={backTo} data-testid="ext-detail-back"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to search results
      </Link>
      )}

      <div className={embedded ? "" : "bg-white border border-zinc-200 rounded-lg p-6 sm:p-8"}>
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-14 w-14 rounded-md flex items-center justify-center font-display font-bold text-xl bg-zinc-100 text-zinc-700">
            {maskedEmployer ? "?" : (job.company_name?.charAt(0)?.toUpperCase() || "?")}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className={`font-display tracking-tight font-bold text-zinc-950 leading-tight ${embedded ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"}`}>{job.title}</h1>
            <p className="text-base font-medium text-zinc-600 mt-1" data-testid="ext-detail-employer">{employerShown}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-sm text-zinc-600" data-testid="ext-detail-fields">
          {job.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-zinc-400" />{job.location}</span>}
          {extType && <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-zinc-400" />{extType}</span>}
          {contract && <span className="inline-flex items-center gap-1.5"><FileText className="h-4 w-4 text-zinc-400" />{contract}</span>}
          {salary && <span className="inline-flex items-center gap-1.5"><BadgePoundSterling className="h-4 w-4 text-zinc-400" />{salary}</span>}
          {closes && <span className="inline-flex items-center gap-1.5" data-testid="ext-detail-closes"><CalendarDays className="h-4 w-4 text-zinc-400" />Closes {closes}</span>}
          <span className="text-xs text-zinc-400 ml-auto">{timeAgo(job.created_at)}</span>
        </div>

        {job.category_label && (
          <span className="inline-block mt-4 text-xs font-medium bg-zinc-100 text-zinc-600 px-2.5 py-1 rounded-full">{job.category_label}</span>
        )}

        {job.description && (
          <div className="mt-6 border-t border-zinc-100 pt-6" data-testid="ext-detail-description">
            {job.source === "nhs" && (
              <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Summary supplied by NHS Jobs</p>
            )}
            <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line">{job.description}</p>
          </div>
        )}

        {job.benefits && (
          <div className="mt-6 border-t border-zinc-100 pt-6" data-testid="ext-detail-benefits">
            <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Benefits</p>
            <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line">{job.benefits}</p>
          </div>
        )}

        {job.locations && job.locations.length > 1 && (
          <p className="mt-4 text-xs text-zinc-500" data-testid="ext-detail-locations">
            Also available in: {job.locations.slice(1).join(" · ")}
          </p>
        )}

        <div className="mt-6 flex flex-wrap gap-2" data-testid="ext-detail-internal-links">
          {town && (
            <Link to={`/jobs/near/${townSlug}`} data-testid="ext-link-area"
              className="px-3 py-1.5 border border-zinc-200 rounded-full text-xs font-medium text-zinc-700 hover:border-zinc-400 hover:text-zinc-950 transition-colors duration-200">
              Jobs near {town}
            </Link>
          )}
          {job.category && (
            <Link to={`/jobs?category=${encodeURIComponent(job.category)}`} data-testid="ext-link-category"
              className="px-3 py-1.5 border border-zinc-200 rounded-full text-xs font-medium text-zinc-700 hover:border-zinc-400 hover:text-zinc-950 transition-colors duration-200">
              More {job.category} jobs
            </Link>
          )}
        </div>

        <div className={embedded ? "sticky bottom-0 z-10 bg-white border-t border-zinc-100 mt-8 pt-4 pb-2 -mx-5 px-5" : "mt-8 border-t border-zinc-100 pt-6"}>
          <a href={applyUrl} target="_blank" rel="noreferrer" data-testid="ext-apply-button"
            className="w-full h-12 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 inline-flex items-center justify-center gap-2">
            Continue application on {meta.label} <ArrowUpRight className="h-4 w-4" />
          </a>
          <p className="mt-3 text-xs text-zinc-500 text-center">
            Opens the official {meta.label} application page in a new tab where your browser allows it, so this page and your search stay open.
            If it opens in this tab instead, your browser's Back button brings you straight back here. No HiredToday account is needed.
          </p>
        </div>
      </div>

      <div className="mt-6">
        <JobAlertControl location={town} category={job.category || ""} label="Get new jobs like this by email" />
      </div>
    </main>
  );
}
